// import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
// import { io, Socket } from 'socket.io-client';
// import { useAuth } from './AuthContext';
// import toast from 'react-hot-toast';

// interface SocketContextType {
//   socket: Socket | null;
//   isConnected: boolean;
//   joinInterview: (interviewId: string, sessionId: string) => void;
//   leaveInterview: () => void;
//   sendAudioChunk: (audioData: string, questionId?: string) => void;
//   sendCandidateResponse: (response: string, questionId?: string) => void;
//   connectWithSession: (interviewId: string, sessionId: string) => void;
// }

// const SocketContext = createContext<SocketContextType | undefined>(undefined);

// const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

// export const SocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
//   const [socket, setSocket] = useState<Socket | null>(null);
//   const [isConnected, setIsConnected] = useState(false);
//   const { token, user } = useAuth();

//   // Connect with JWT token (for authenticated users)
//   useEffect(() => {
//     if (token && user) {
//       const newSocket = io(SOCKET_URL, {
//         auth: {
//           token
//         },
//         transports: ['websocket']
//       });

//       setupSocketListeners(newSocket);
//       setSocket(newSocket);

//       return () => {
//         newSocket.close();
//       };
//     }
//   }, [token, user]);

//   const setupSocketListeners = (newSocket: Socket) => {

//     newSocket.on('connect', () => {
//       console.log('Socket connected');
//       setIsConnected(true);
//     });

//     newSocket.on('disconnect', () => {
//       console.log('Socket disconnected');
//       setIsConnected(false);
//     });

//     newSocket.on('connect_error', (error) => {
//       console.error('Socket connection error:', error);
//       toast.error('Connection error. Please refresh the page.');
//     });

//     newSocket.on('transcription', (data) => {
//       console.log('Received transcription:', data);
//       // Handle real-time transcription updates
//     });

//     newSocket.on('ai-response', (data) => {
//       console.log('Received AI response:', data);
//       // Handle AI responses
//     });

//     newSocket.on('error', (error) => {
//       console.error('Socket error:', error);
//       toast.error(error.message || 'An error occurred');
//     });
//   };

//   // Connect with sessionId (for direct access without authentication)
//   const connectWithSession = (interviewId: string, sessionId: string) => {
//     if (socket && socket.connected) {
//       socket.disconnect();
//     }
//     if (socket) {
//       socket.close();
//     }
//     if (!interviewId || !sessionId) {
//       console.warn("Missing interviewId/sessionId. Skipping socket connection.");
//       return;
//     }
  
//     console.log(interviewId,sessionId)
//     console.log(SOCKET_URL)
//     const newSocket = io(SOCKET_URL, {
//       auth: {
//         sessionId,
//         interviewId
//       },
//       transports: ['websocket'],
//       reconnection: true,
//       reconnectionAttempts: 5,
//   reconnectionDelay: 1000,
//     });

//     setupSocketListeners(newSocket);
//     setSocket(newSocket);
//   };

//   const joinInterview = (interviewId: string, sessionId: string) => {
//     if (socket) {
//       socket.emit('join-interview', { interviewId, sessionId });
//     }
//   };

//   const leaveInterview = () => {
//     if (socket) {
//       socket.emit('leave-interview');
//     }
//   };

//   const sendAudioChunk = (audioData: string, questionId?: string) => {
//     if (socket && isConnected) {
//       socket.emit('audio-chunk', {
//         audioData,
//         questionId
//       });
//     }
//   };

//   const sendCandidateResponse = (response: string, questionId?: string) => {
//     if (socket && isConnected) {
//       socket.emit('candidate-response', {
//         response,
//         questionId
//       });
//     }
//   };

//   const value: SocketContextType = {
//     socket,
//     isConnected,
//     joinInterview,
//     leaveInterview,
//     sendAudioChunk,
//     sendCandidateResponse,
//     connectWithSession
//   };

//   return (
//     <SocketContext.Provider value={value}>
//       {children}
//     </SocketContext.Provider>
//   );
// };

// export const useSocket = () => {
//   const context = useContext(SocketContext);
//   if (context === undefined) {
//     throw new Error('useSocket must be used within a SocketProvider');
//   }
//   return context;
// };











import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Socket } from "socket.io-client";
import toast from "react-hot-toast";
import { socket } from "./socketInstance"; // shared singleton
import socketInterceptor from "../utils/socketInterceptor";
import { useAuth } from "./AuthContext";

interface SocketContextType {
  socket: Socket;
  isConnected: boolean;
  joinInterview: (interviewId: string, sessionId: string) => void;
  leaveInterview: () => void;
  sendAudioChunk: (audioData: string, questionId?: string) => void;
  sendCandidateResponse: (response: string, questionId?: string) => void;
  connectWithSession: (interviewId: string, sessionId: string) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!socket) return;

    const setupListeners = () => {
      // Note: Interceptors already handle connect/disconnect/error events
      // These are additional listeners for component state management
      socket.on("connect", () => {
        console.log("✅ Socket connected:", socket.id);
        setIsConnected(true);
      });

      socket.on("disconnect", (reason) => {
        console.log("❌ Socket disconnected:", reason);
        setIsConnected(false);
      });

      socket.on("connect_error", (err) => {
        console.error("Socket connect_error:", err.message);
        // Error toast is handled by interceptor, but we can add additional handling here if needed
      });
    };

    setupListeners();

    return () => {
      // Don't remove all listeners as interceptors need them
      // Only remove our specific listeners
      socket.off("connect");
      socket.off("disconnect");
      socket.off("connect_error");
    };
  }, []);

  const connectWithSession = async (interviewId: string, sessionId: string) => {
    if (!interviewId || !sessionId) {
      console.warn("Missing interviewId/sessionId, skipping connect.");
      return;
    }

    if (socket.connected) socket.disconnect(); // ensure clean state
    
    // Prepare auth config with userId from AuthContext if available
    const authConfig: any = { interviewId, sessionId };
    if (user?.id) {
      authConfig.userId = user.id;
    }
    
    // Apply request interceptors to add auth token, userId, etc.
    const config = await socketInterceptor.applyRequestInterceptors({
      auth: authConfig,
    });
    
    // Update socket auth with intercepted config (includes token, userId from interceptor)
    socket.auth = config.auth || { interviewId, sessionId };
    socket.connect();
  };

  const joinInterview = (interviewId: string, sessionId: string) => {
    if (socket.connected) {
      socket.emit("join-interview", { interviewId, sessionId });
    }
  };

  const leaveInterview = () => {
    if (socket.connected) socket.emit("leave-interview");
  };

  const sendAudioChunk = (audioData: string, questionId?: string) => {
    if (socket.connected) socket.emit("audio-chunk", { audioData, questionId });
  };

  const sendCandidateResponse = (response: string, questionId?: string) => {
    if (socket.connected) socket.emit("candidate-response", { response, questionId });
  };

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        joinInterview,
        leaveInterview,
        sendAudioChunk,
        sendCandidateResponse,
        connectWithSession,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error("useSocket must be used within SocketProvider");
  return ctx;
};
